import { Box, Typography, useMediaQuery } from "@mui/material";

import whyMandala from "../../../assets/Images/Temples/why-mandala.svg";
import { HINDI_FONT, LotusDivider, SectionTag, SwipeRow, TEMPLE_COLORS } from "../shared";
import { spin } from "../TempleHero/motion";

// Arch card; `m` switches to the mobile sizes.
const ArchCard = ({ card, m }) => (
  <Box
    sx={{
      minHeight: m ? 272 : 392,
      height: m ? "100%" : "auto",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: m ? "10px" : "14px",
      pt: m ? "52px" : "84px",
      pb: m ? "28px" : "44px",
      px: m ? "24px" : "36px",
      textAlign: "center",
      background: "#FFFFFF",
      border: "1px solid rgba(201, 149, 43, 0.55)",
      borderRadius: m ? "145px 145px 16px 16px" : "189px 189px 20px 20px",
      boxShadow: m ? "none" : "0px 16px 36px 0px rgba(102, 64, 26, 0.06)",
    }}
  >
    <Box
      sx={{
        width: m ? 56 : 68,
        height: m ? 56 : 68,
        borderRadius: m ? "28px" : "34px",
        background: TEMPLE_COLORS.blush,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Box component="img" src={card.icon} alt="" sx={{ maxWidth: m ? 26 : 30, maxHeight: m ? 26 : 30 }} />
    </Box>
    <Typography sx={{ fontFamily: HINDI_FONT, fontSize: m ? "16px" : "18px", lineHeight: m ? "22px" : "26px", color: TEMPLE_COLORS.gold }}>
      {card.hindiLabel}
    </Typography>
    <Typography component="h3" sx={{ fontWeight: 700, fontSize: m ? "16px" : "22px", lineHeight: m ? "22px" : "30px", color: TEMPLE_COLORS.ink }}>
      {card.title}
    </Typography>
    <Typography sx={{ fontSize: m ? "13px" : "15px", lineHeight: m ? "20px" : "24px", color: TEMPLE_COLORS.muted }}>
      {card.description}
    </Typography>
  </Box>
);

const WhyTemplesSection = ({ content }) => {
  const isTablet = useMediaQuery("(max-width: 1199px)");
  const isMobile = useMediaQuery("(max-width: 599px)");

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "#FBF5EC",
        mt: isMobile ? 0 : "40px",
        px: isMobile ? "20px" : isTablet ? "40px" : "120px",
        py: isMobile ? "76px" : "100px",
      }}
    >
      {!isMobile && (
        <Box
          component="img"
          src={whyMandala}
          alt=""
          sx={{
            position: "absolute",
            left: "calc(50% + 195.2px)",
            top: "-184.8px",
            width: 731,
            height: 731,
            pointerEvents: "none",
            // Turns like the hero mandalas, at half their speed.
            animation: `${spin} 180s linear infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isMobile ? "28px" : "56px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isMobile ? "12px" : "18px",
            maxWidth: 820,
            textAlign: "center",
          }}
        >
          <SectionTag small={isMobile}>{content.tag}</SectionTag>
          <Typography
            component="h2"
            sx={{ fontWeight: 700, fontSize: isMobile ? "28px" : "46px", lineHeight: isMobile ? "36px" : "56px", color: TEMPLE_COLORS.ink }}
          >
            {content.titleStart}{" "}
            <Box component="span" sx={{ color: TEMPLE_COLORS.red }}>
              {content.titleHighlight}
            </Box>
          </Typography>
          {isMobile && <LotusDivider small />}
          <Typography sx={{ fontSize: isMobile ? "15px" : "17px", lineHeight: isMobile ? "24px" : "29px", color: TEMPLE_COLORS.body }}>
            {content.description}
          </Typography>
        </Box>

        {isMobile ? (
          <Box sx={{ width: "calc(100% + 40px)", mx: "-20px", display: "flex", flexDirection: "column", gap: "28px" }}>
            <SwipeRow itemWidth={290} px={20}>
              {content.cards.map((card) => (
                <ArchCard key={card.title} card={card} m />
              ))}
            </SwipeRow>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isTablet ? "1fr" : "repeat(3, 378px)",
              justifyContent: "space-between",
              gap: "33px",
              width: "100%",
              maxWidth: isTablet ? 420 : "none",
            }}
          >
            {content.cards.map((card) => (
              <ArchCard key={card.title} card={card} />
            ))}
          </Box>
        )}

        {content.quote && (
          <Typography
            sx={{
              fontWeight: 700,
              fontStyle: "italic",
              fontSize: isMobile ? "15px" : "20px",
              lineHeight: isMobile ? "23px" : "30px",
              color: TEMPLE_COLORS.ink,
              textAlign: "center",
            }}
          >
            {content.quote}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default WhyTemplesSection;
